/**
 * Tenant portal app (app.amberpeak.ca). Auth0-only; all routes require login
 * except /callback (Auth0 redirect), /impersonate (admin impersonation),
 * and /assisted-login (staff-issued one-time link).
 */
import React, { useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import CallbackPage from './pages/CallbackPage';
import AssistedLoginPage from './pages/AssistedLoginPage';
import TenantLayout from './components/tenant/TenantLayout';
import ImpersonationLayout from './components/tenant/ImpersonationLayout';
import TenantDashboardPage from './pages/TenantDashboardPage';
import MyInformationPage from './pages/MyInformationPage';
import DiscoveryCallPage from './pages/DiscoveryCallPage';
import ShortlistUnitsPage from './pages/ShortlistUnitsPage';
import BookToursPage from './pages/BookToursPage';
import AnalyzeUnitsPage from './pages/AnalyzeUnitsPage';
import ApplyToUnitPage from './pages/ApplyToUnitPage';
import SelectionPage from './pages/SelectionPage';
import LeaseSigningPage from './pages/LeaseSigningPage';
import MoveOutPage from './pages/MoveOutPage';
import MoveInPage from './pages/MoveInPage';
import { pathWithStoredGoogleAdsClickIds } from './utils/googleAdsClick';
import { rememberWebsiteFavorites } from './utils/websiteFavorites';
import { rememberWebsiteHiveProspectIntent } from './utils/websiteHiveProspectIntent';
import { TenantAppearanceProvider } from './contexts/TenantAppearanceContext';
import TenantPortalSessionGuard from './components/tenant/TenantPortalSessionGuard';

const AUTH0_CALLBACK_PATH = import.meta.env.VITE_AUTH0_CALLBACK_PATH || '/callback';
const auth0Configured = !!(
  import.meta.env.VITE_AUTH0_DOMAIN && import.meta.env.VITE_AUTH0_CLIENT_ID
);

function RememberWebsiteFavorites(): null {
  const { search } = useLocation();
  useEffect(() => {
    rememberWebsiteFavorites(search);
  }, [search]);
  return null;
}

function RememberWebsiteHiveProspectIntent(): null {
  const { search } = useLocation();
  useEffect(() => {
    rememberWebsiteHiveProspectIntent(search);
  }, [search]);
  return null;
}

function AppRoutes() {
  return (
    <>
    <RememberWebsiteFavorites />
    <RememberWebsiteHiveProspectIntent />
    <Routes>
      <Route path={AUTH0_CALLBACK_PATH} element={<CallbackPage />} />
      <Route path="/assisted-login" element={<AssistedLoginPage />} />

      {/* Admin impersonation — bypasses Auth0, uses impersonation JWT */}
      <Route path="/impersonate" element={<ImpersonationLayout />}>
        <Route index element={<TenantDashboardPage />} />
        <Route path="my-information" element={<MyInformationPage />} />
        <Route path="discovery-call" element={<DiscoveryCallPage />} />
        <Route path="shortlist-units" element={<ShortlistUnitsPage />} />
        <Route path="book-tours" element={<BookToursPage />} />
        <Route path="analyze-units" element={<AnalyzeUnitsPage />} />
        <Route path="apply-to-unit" element={<ApplyToUnitPage />} />
        <Route path="selection" element={<SelectionPage />} />
        <Route path="lease-signing" element={<LeaseSigningPage />} />
        <Route path="move-out" element={<MoveOutPage />} />
        <Route path="move-in" element={<MoveInPage />} />
      </Route>

      {/* Normal Auth0-protected routes */}
      <Route path="/" element={<TenantLayout />}>
        <Route index element={<TenantDashboardPage />} />
        <Route path="my-information" element={<MyInformationPage />} />
        <Route path="discovery-call" element={<DiscoveryCallPage />} />
        <Route path="shortlist-units" element={<ShortlistUnitsPage />} />
        <Route path="book-tours" element={<BookToursPage />} />
        <Route path="analyze-units" element={<AnalyzeUnitsPage />} />
        <Route path="apply-to-unit" element={<ApplyToUnitPage />} />
        <Route path="selection" element={<SelectionPage />} />
        <Route path="lease-signing" element={<LeaseSigningPage />} />
        <Route path="move-out" element={<MoveOutPage />} />
        <Route path="move-in" element={<MoveInPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  const navigate = useNavigate();

  if (!auth0Configured) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center text-gray-600 dark:text-gray-400">
          <p>Tenant portal is not configured.</p>
          <p className="mt-2 text-sm">Set VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Auth0Provider
        domain={import.meta.env.VITE_AUTH0_DOMAIN!}
        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID!}
        authorizationParams={{
          redirect_uri:
            typeof window !== 'undefined'
              ? `${window.location.origin}${AUTH0_CALLBACK_PATH}`
              : undefined,
          audience: import.meta.env.VITE_AUTH0_AUDIENCE || undefined,
          ui_locales: 'en-CA',
        }}
        onRedirectCallback={(appState) => {
          navigate(pathWithStoredGoogleAdsClickIds(appState?.returnTo || '/'));
        }}
      >
        <TenantAppearanceProvider>
          <TenantPortalSessionGuard>
            <AppRoutes />
          </TenantPortalSessionGuard>
        </TenantAppearanceProvider>
      </Auth0Provider>
    </div>
  );
}

export default App;
